import React, { useState } from 'react';
import {
  Send, CheckCircle2, XCircle, Mail, Download,
  AlertTriangle, Clock, X, FileText, Loader2, Copy,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { invoicesAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';

const hFont = { fontFamily: 'Manrope, sans-serif' };

const STATUS_COLORS = {
  draft: 'bg-stone-100 text-stone-700 border-stone-300',
  sent: 'bg-blue-50 text-blue-700 border-blue-200',
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  overdue: 'bg-amber-50 text-amber-700 border-amber-200',
  cancelled: 'bg-red-50 text-red-700 border-red-200',
};

const REMINDER_LABELS = { 1: 'First Reminder', 2: 'Second Reminder', 3: 'Final Reminder' };

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InvoiceDetailSheet({ invoice, onAction, onClose }) {
  const [sendingReminder, setSendingReminder] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  if (!invoice) return null;

  const currentLevel = invoice.reminder_level || 0;
  const nextLevel = Math.min(currentLevel + 1, 3);
  const canRemind = ['sent', 'overdue'].includes(invoice.status) && currentLevel < 3;

  const handleReminder = async (level) => {
    setSendingReminder(true);
    try {
      await invoicesAPI.sendReminder(invoice.id, level);
      toast.success(`${REMINDER_LABELS[level]} sent`);
      onAction('refresh');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminder');
    }
    setSendingReminder(false);
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const url = `${API_URL}/api/invoices/${invoice.id}/pdf`;
      const resp = await fetch(url, { credentials: 'include' });
      if (!resp.ok) throw new Error('PDF generation failed');
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `invoice_${invoice.invoice_number}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error(err.message || 'Failed to download PDF');
    }
    setDownloading(false);
  };

  const handleActionWithLoading = async (action) => {
    setActionLoading(action);
    await onAction(action, invoice);
    setActionLoading(null);
  };

  const handleDuplicate = async () => {
    setActionLoading('duplicate');
    try {
      await invoicesAPI.create({
        customer_id: invoice.customer_id,
        invoice_date: new Date().toISOString().slice(0, 10),
        due_date: (() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); })(),
        lines: invoice.lines.map(l => ({
          description: l.description,
          quantity: l.quantity,
          unit_price: l.unit_price,
          vat_rate: l.vat_rate,
        })),
        notes: invoice.notes || '',
      });
      toast.success('Invoice duplicated as new draft');
      onAction('refresh');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to duplicate');
    }
    setActionLoading(null);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
        data-testid="invoice-detail-sheet"
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100 bg-stone-50/50 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[#D94E18]/10 flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-[#D94E18]" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-stone-900 truncate" style={hFont}>{invoice.invoice_number}</h2>
              <p className="text-xs text-stone-500 truncate">{invoice.customer_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`text-[10px] font-semibold ${STATUS_COLORS[invoice.status] || ''}`}>
              {invoice.status}
            </Badge>
            <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center" data-testid="close-invoice-sheet">
              <X className="w-4 h-4 text-stone-500" />
            </button>
          </div>
        </div>

        {/* ── Scrollable Body ── */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Reminder banner */}
          {invoice.reminder_level > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <span className="text-xs text-amber-800 font-medium">
                {REMINDER_LABELS[invoice.reminder_level]} sent{invoice.last_reminder_at ? ` on ${formatDate(invoice.last_reminder_at)}` : ''}
              </span>
            </div>
          )}

          {/* Invoice Info Grid */}
          <div className="grid grid-cols-2 gap-4">
            <InfoCell label="Customer" value={invoice.customer_name} />
            <InfoCell label="Email" value={invoice.customer_email || '—'} />
            <InfoCell label="Invoice Date" value={formatDate(invoice.invoice_date)} />
            <InfoCell label="Due Date" value={formatDate(invoice.due_date)} />
          </div>

          {/* Line Items */}
          <div className="bg-stone-50 rounded-xl p-4">
            <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-3">Line Items</p>
            <div className="space-y-3">
              {invoice.lines?.map((l) => (
                <div key={`${l.description}-${l.quantity}-${l.unit_price}`} className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-stone-800 font-medium leading-tight">{l.description}</p>
                    <p className="text-[11px] text-stone-400 mt-0.5">
                      {l.quantity} x {formatCurrency(l.unit_price)} · {l.vat_rate}% VAT
                    </p>
                  </div>
                  <p className="text-sm font-mono font-bold text-stone-900 flex-shrink-0">
                    {formatCurrency(l.gross_amount)}
                  </p>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="border-t border-stone-200 mt-4 pt-3 space-y-1.5">
              <div className="flex justify-between text-xs text-stone-500">
                <span>Subtotal</span>
                <span className="font-mono">{formatCurrency(invoice.subtotal)}</span>
              </div>
              <div className="flex justify-between text-xs text-stone-500">
                <span>VAT</span>
                <span className="font-mono">{formatCurrency(invoice.total_vat)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold text-stone-900 pt-1">
                <span>Total</span>
                <span className="font-mono text-[#D94E18]">{formatCurrency(invoice.total)}</span>
              </div>
            </div>
          </div>

          {invoice.notes && (
            <div className="bg-stone-50 rounded-xl p-4">
              <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-1">Notes</p>
              <p className="text-sm text-stone-600">{invoice.notes}</p>
            </div>
          )}
        </div>

        {/* ── Sticky Bottom Actions ── */}
        <div className="border-t border-stone-100 px-5 py-4 bg-white flex-shrink-0 safe-area-bottom">
          {/* Primary row: PDF + Send/Post */}
          <div className="flex gap-2 mb-2">
            <Button
              variant="outline"
              className="flex-1 h-12 rounded-xl text-sm font-semibold"
              onClick={handleDownloadPdf}
              disabled={downloading}
              data-testid="download-pdf-btn"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
              Download PDF
            </Button>

            {invoice.status === 'draft' && (
              <Button
                className="flex-1 h-12 rounded-xl text-sm font-semibold bg-[#D94E18] hover:bg-[#C2410C] text-white"
                onClick={() => handleActionWithLoading('send')}
                disabled={!!actionLoading}
                data-testid="finalize-send-btn"
              >
                {actionLoading === 'send' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Finalize & Send
              </Button>
            )}

            {['sent', 'overdue'].includes(invoice.status) && (
              <Button
                className="flex-1 h-12 rounded-xl text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => handleActionWithLoading('paid')}
                disabled={!!actionLoading}
                data-testid="mark-paid-btn"
              >
                {actionLoading === 'paid' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Mark Paid
              </Button>
            )}
          </div>

          {/* Secondary row: contextual actions */}
          <div className="flex gap-2">
            {invoice.status === 'draft' && (
              <>
                <Button
                  variant="outline"
                  className="flex-1 h-10 rounded-xl text-xs"
                  onClick={() => handleActionWithLoading('post')}
                  disabled={!!actionLoading}
                  data-testid="post-gl-btn"
                >
                  {actionLoading === 'post' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                  Post to GL
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 h-10 rounded-xl text-xs text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => handleActionWithLoading('cancel')}
                  disabled={!!actionLoading}
                  data-testid="cancel-invoice-btn"
                >
                  <XCircle className="w-3.5 h-3.5 mr-1.5" /> Cancel
                </Button>
              </>
            )}

            {['sent', 'overdue'].includes(invoice.status) && (
              <>
                <Button
                  variant="outline"
                  className="flex-1 h-10 rounded-xl text-xs"
                  onClick={() => handleActionWithLoading('send')}
                  disabled={!!actionLoading}
                  data-testid="resend-btn"
                >
                  <Mail className="w-3.5 h-3.5 mr-1.5" /> Resend
                </Button>
                {canRemind && (
                  <Button
                    variant="outline"
                    className="flex-1 h-10 rounded-xl text-xs text-amber-700 border-amber-200 hover:bg-amber-50"
                    onClick={() => handleReminder(nextLevel)}
                    disabled={sendingReminder}
                    data-testid="send-reminder-btn"
                  >
                    <Clock className="w-3.5 h-3.5 mr-1.5" />
                    {sendingReminder ? 'Sending...' : REMINDER_LABELS[nextLevel]}
                  </Button>
                )}
              </>
            )}

            <Button
              variant="outline"
              className="h-10 rounded-xl text-xs px-3"
              onClick={handleDuplicate}
              disabled={!!actionLoading}
              data-testid="duplicate-invoice-btn"
            >
              {actionLoading === 'duplicate' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoCell({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider">{label}</p>
      <p className="text-sm font-medium text-stone-800 mt-0.5">{value}</p>
    </div>
  );
}
